import React from 'react';
import { Card, Badge, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisV,
  faEdit,
  faTrash,
  faChartLine,
  faCalendarAlt,
  faCheckCircle,
  faTimesCircle,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons';

/**
 * Parses a weekly cron expression ("minute hour * * dayOfWeek") into a
 * human-readable string, e.g. "Every Monday at 09:30".
 */
const parseCronToHuman = (cron) => {
  if (!cron) return '—';
  const parts = cron.trim().split(/\s+/);
  if (parts.length < 5) return cron;

  const [minute, hour, , , dow] = parts;

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = days[parseInt(dow, 10)] ?? `Day ${dow}`;

  const h = String(hour).padStart(2, '0');
  const m = String(minute).padStart(2, '0');

  return `Every ${dayName} at ${h}:${m}`;
};

const ReportRuleCard = ({ rule, onEdit, onDelete }) => {
  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit(rule);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(rule);
  };

  const totalRecipients =
    (rule.recipients?.emails?.length ?? 0) + (rule.recipients?.cc?.length ?? 0);

  return (
    <Card className="alert-rule-card h-100">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
          <FontAwesomeIcon icon={faChartLine} className="me-2 text-primary flex-shrink-0" />
          <h6 className="mb-0 text-truncate" title={rule.name}>
            {rule.name}
          </h6>
        </div>

        <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
          <Dropdown.Toggle
            variant="link"
            className="text-muted p-0 no-caret"
            id={`report-rule-dropdown-${rule.id}`}
          >
            <FontAwesomeIcon icon={faEllipsisV} />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={handleEdit}>
              <FontAwesomeIcon icon={faEdit} className="me-2" />
              Edit
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={handleDelete} className="text-danger">
              <FontAwesomeIcon icon={faTrash} className="me-2" />
              Delete
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </Card.Header>

      <Card.Body>
        {/* Status */}
        <div className="mb-3">
          <Badge bg={rule.isEnabled ? 'success' : 'secondary'} className="d-inline-flex align-items-center gap-1">
            <FontAwesomeIcon icon={rule.isEnabled ? faCheckCircle : faTimesCircle} />
            {rule.isEnabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </div>

        {/* Report Type */}
        <div className="mb-2">
          <small className="text-muted d-block">Report Type</small>
          <span className="fw-medium">{rule.reportType?.replace(/_/g, ' ')}</span>
        </div>

        {/* Cadence */}
        <div className="mb-2">
          <small className="text-muted d-block">Schedule</small>
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-info" size="sm" />
            <span className="fw-medium">{parseCronToHuman(rule.cronExpression)}</span>
          </div>
        </div>

        {/* Criteria summary */}
        {rule.criteria && (
          <div className="mb-2">
            <small className="text-muted d-block">Criteria</small>
            <div className="criteria-summary">
              {rule.criteria.module && (
                <Badge bg="light" text="dark" className="me-1 mb-1">
                  {rule.criteria.module}
                </Badge>
              )}
              {rule.criteria.meterId?.length > 0 && (
                <Badge bg="info" className="me-1 mb-1">
                  {rule.criteria.meterId.length} Meter{rule.criteria.meterId.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Recipients */}
        <div className="mb-0">
          <small className="text-muted d-block">Recipients</small>
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faEnvelope} className="text-secondary" size="sm" />
            <span className="fw-medium">
              {totalRecipients} recipient{totalRecipients !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </Card.Body>

      <Card.Footer className="text-muted">
        <small>Updated: {new Date(rule.updatedAt).toLocaleDateString()}</small>
      </Card.Footer>
    </Card>
  );
};

export default ReportRuleCard;